import { record } from 'rrweb';
import type { eventWithTime } from '@rrweb/types';

interface RecorderOptions {
  apiUrl: string;
  sessionId: string;
  sessionToken: string;
  flushIntervalMs?: number;
  maxBatchSize?: number;
  /**
   * Cluster K item 89 — invoked when MAX_FIRST_FLUSH_ATTEMPTS is exhausted
   * without a successful initial POST. The parent uses this to tag the
   * session as "recording-incomplete" so the dashboard's replay player can
   * show a clear "this demo wasn't recorded" message instead of failing
   * silently. Best-effort; the recorder keeps trying on subsequent flushes.
   */
  onFirstFlushExhausted?: () => void;
}

/**
 * Captures DOM mutations + user input via rrweb and forwards batched events
 * to the platform via HTTPS POST. We avoid WebSockets so customer-site CSPs
 * that allow https://*.iraa.ai (but not wss://) work out of the box.
 *
 * Inputs are masked by default (rrweb's maskAllInputs); the customer can
 * adjust via PDP config later if they need finer-grained masking.
 */
export class Recorder {
  private stop: (() => void) | null = null;
  private buffer: eventWithTime[] = [];
  private timer: number | null = null;
  private opts: Required<Omit<RecorderOptions, 'onFirstFlushExhausted'>> & {
    onFirstFlushExhausted?: () => void;
  };
  private firstFlushExhaustedFired = false;
  private started = false;
  // Until the first POST returns 2xx we keep failed batches around and retry —
  // the very first batch is the one carrying Meta + FullSnapshot, and losing
  // it makes the entire recording unplayable. After the first success we fail
  // open as before: a missed mid-session batch only costs a few seconds of
  // replay fidelity.
  private firstFlushSucceeded = false;
  private flushInFlight = false;
  // Hard cap so a hard-down server doesn't blow up memory while we keep
  // re-prepending events in retry mode. ~5x the batch size is plenty.
  private static readonly MAX_RETAINED_EVENTS = 1000;
  private static readonly MAX_FIRST_FLUSH_ATTEMPTS = 5;
  // keepalive fetches have a ~64KB body cap in most browsers. Stay under it
  // so a FullSnapshot batch (which can be tens of KB) never silently drops on
  // pagehide. Above this we drop keepalive and use a normal fetch — best
  // effort on unload but reliable for normal interval flushes.
  private static readonly KEEPALIVE_BYTE_BUDGET = 60_000;

  constructor(opts: RecorderOptions) {
    this.opts = {
      apiUrl: opts.apiUrl,
      sessionId: opts.sessionId,
      sessionToken: opts.sessionToken,
      flushIntervalMs: opts.flushIntervalMs ?? 5000,
      maxBatchSize: opts.maxBatchSize ?? 200,
      onFirstFlushExhausted: opts.onFirstFlushExhausted,
    };
  }

  start(): void {
    if (this.started) return;
    this.started = true;

    this.stop = record({
      emit: (event) => {
        this.buffer.push(event);
        if (this.buffer.length >= this.opts.maxBatchSize) void this.flush();
      },
      maskAllInputs: true,
      sampling: {
        // Cluster K item 90 — 50→25ms doubles cursor fidelity for replay.
        // Cost is small (rrweb's mousemove events are tiny) and the result
        // makes intent-highlight overlays land where the cursor actually
        // was instead of in a between-frame interpolation.
        mousemove: 25,
        scroll: 150,
        input: 'last',
      },
      recordCanvas: false,
      collectFonts: false,
      // Force a fresh Meta + FullSnapshot every 30s. Keeps the recording
      // playable even if the very first batch (which originally carries
      // Meta+FullSnapshot) is lost — the next checkout produces a new
      // baseline within 30s and the replay starts from there.
      checkoutEveryNms: 30_000,
    }) ?? null;

    this.timer = window.setInterval(() => { void this.flush(); }, this.opts.flushIntervalMs);
    window.addEventListener('pagehide', this.handleUnload);
    window.addEventListener('beforeunload', this.handleUnload);
  }

  shutdown(): void {
    if (!this.started) return;
    this.started = false;

    window.removeEventListener('pagehide', this.handleUnload);
    window.removeEventListener('beforeunload', this.handleUnload);
    if (this.timer != null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    if (this.stop) {
      try { this.stop(); } catch { /* ignore */ }
      this.stop = null;
    }
    void this.flush({ unload: true });
  }

  private handleUnload = (): void => {
    void this.flush({ unload: true });
  };

  private async flush(opts: { unload?: boolean } = {}): Promise<void> {
    if (this.buffer.length === 0) return;
    // Avoid stacking concurrent flushes — the interval timer can fire while a
    // retry chain is still working on the same batch.
    if (this.flushInFlight) return;

    const events = this.buffer;
    this.buffer = [];
    this.flushInFlight = true;

    try {
      if (this.firstFlushSucceeded) {
        // Steady-state: fire-and-forget, swallow errors.
        this.postBatch(events, { unload: !!opts.unload }).catch(() => undefined);
        return;
      }

      // First-batch retry path — this is what protects Meta + FullSnapshot
      // against the server-registration race on session start.
      let attempt = 0;
      while (attempt < Recorder.MAX_FIRST_FLUSH_ATTEMPTS) {
        attempt += 1;
        try {
          await this.postBatch(events, { unload: !!opts.unload });
          this.firstFlushSucceeded = true;
          return;
        } catch {
          if (attempt >= Recorder.MAX_FIRST_FLUSH_ATTEMPTS || opts.unload) break;
          // 1s, 2s, 4s, 8s, 16s — capped at MAX_FIRST_FLUSH_ATTEMPTS.
          const delayMs = 1000 * 2 ** (attempt - 1);
          await new Promise((r) => setTimeout(r, delayMs));
        }
      }
      // All retries exhausted — re-prepend and let the next interval/maxBatch
      // tick try again. New events emitted in the meantime sit on the end.
      // Cluster K item 89 — fire the parent callback once so the dashboard
      // can mark the session unplayable. We keep retrying in the background;
      // if a later flush succeeds, the recording is salvageable for any
      // events captured after the next checkout (every 30s).
      if (!this.firstFlushExhaustedFired) {
        this.firstFlushExhaustedFired = true;
        try {
          this.opts.onFirstFlushExhausted?.();
        } catch { /* never let a callback error break the recorder */ }
      }
      const merged = events.concat(this.buffer);
      const overflow = merged.length - Recorder.MAX_RETAINED_EVENTS;
      this.buffer = overflow > 0 ? merged.slice(overflow) : merged;
    } finally {
      this.flushInFlight = false;
    }
  }

  private async postBatch(
    events: eventWithTime[],
    { unload }: { unload: boolean },
  ): Promise<void> {
    const body = JSON.stringify({ events });
    // keepalive lets a flush survive page unload, but its body cap (~64KB)
    // would silently drop a FullSnapshot batch. Use it only when small, and
    // only when actually unloading.
    const useKeepalive = unload && body.length <= Recorder.KEEPALIVE_BYTE_BUDGET;

    const res = await fetch(`${this.opts.apiUrl}/api/v1/session/${this.opts.sessionId}/record`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${this.opts.sessionToken}`,
      },
      body,
      keepalive: useKeepalive,
    });
    if (!res.ok) {
      throw new Error(`record POST ${res.status}`);
    }
  }
}
